import { useMutation } from '@tanstack/react-query'
import { useSessionStore } from '@/features/application-session'
import { dhaRequest } from '@/lib/http/dha/dhaRequest'
import { vfsClientRoutes } from '@/lib/http/vfsclient/routes'

/**
 * §8.1 Cancel application.
 *
 * No `appRefNo` in the body: the gateway rejects it as an unknown field, the same
 * shape proven on §4.3 and applied throughout this client (docs/api-gaps.md #17).
 * `Reason` is mandatory free text, ≤500 chars — the confirm dialog (board
 * 2182:27606) asks the citizen to confirm, not to explain, so a fixed value fills
 * the field the spec requires.
 *
 * ⚠ `?idNo=` IS required here, whatever the spec says. Do not remove it again.
 *
 * The DHA spec documents the request as `{ reason }` alone, the Postman collection
 * sends no query string, and the MSW handler does not read one — so on 2026-09-10 it
 * was dropped on all three authorities at once. The live gateway answered:
 *
 *     {"title": "One or more validation errors occurred.", "status": 400,
 *      "errors": {"idNo": ["The idNo field is required."]}}
 *
 * That is ASP.NET model validation from VFS's own controller rather than from DHA,
 * which is why it appears in none of the DHA-side material. The mock disagrees with
 * production here; see the note on the handler.
 *
 * This is also why `clearApplicantData()` no longer nulls `idNo`: the button lives on
 * the SUBMITTED receipt and nowhere else, and until that carve-out the one screen
 * that offers cancellation was the one screen with nothing to cancel with.
 *
 * Answers 204, like every other write endpoint in this chapter.
 *
 * Unlike delivery-details and finalise, a 409 here is a GENUINE error and is not
 * absorbed by §4.8 — cancellation locks once the application reaches SENT_TO_PRINT.
 * So there is deliberately no conflict branch below.
 */
export function cancelApplication(
  appRefNo: string,
  idNo: string,
  reason: string,
  signal?: AbortSignal
): Promise<void> {
  return dhaRequest({
    path: vfsClientRoutes.application.cancel(appRefNo),
    idNo,
    body: { reason },
    signal,
  })
}

export function useCancelApplication(appRefNo: string | undefined) {
  const idNo = useSessionStore((s) => s.idNo)

  return useMutation({
    // Both guards are unreachable from the button, which refuses to call `mutate`
    // without either value and says so in words the citizen can act on. They stay
    // as a backstop for a future caller, and deliberately throw rather than
    // resolving: a cancel that silently does nothing is the worse failure.
    mutationFn: (reason: string) => {
      if (!appRefNo) throw new Error('Cannot cancel an application without a reference number.')
      if (!idNo) throw new Error('Cannot cancel an application without an ID number.')
      return cancelApplication(appRefNo, idNo, reason)
    },
    // Not idempotent and no idempotency key exists, same as every other write
    // endpoint here — the citizen presses Confirm again if it fails.
    retry: false,
  })
}
